import type { AxiosError } from "axios";
import Axios from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { lezKeys } from "src/query-keys";
import type {
  APIError,
  FleetConfig,
  FleetPercentageApiItem,
  FleetPercentageCategory,
  FleetPercentageItem,
  LEZ,
  PredefinedLEZOption,
} from "src/types";

export type LEZMutationData = Omit<LEZ, "id" | "fleetType">;
export type FleetConfigMutationData = FleetConfig;

/**
 * Deserialize the json format into a Javascript object
 */

function deserialize(data: LEZsResponse): LEZsResponse {
  return {
    ...data,
  };
}

interface LEZsResponse {
  geoserverFilter: number;
  zones: LEZ[];
}
export function useLEZs(scenarioId: number | string) {
  async function queryFn(scenarioId: number | string): Promise<LEZsResponse> {
    const response = await Axios.get(
      `/planning-api/scenarios/${scenarioId}/zones`
    );
    return deserialize(response.data);
  }
  return useQuery<LEZsResponse, AxiosError<APIError[]>>(
    lezKeys.all(scenarioId),
    () => queryFn(scenarioId)
  );
}

export function useLEZ(scenarioId: number | string, lezId: number | string) {
  async function queryFn(scenarioId: number | string): Promise<LEZsResponse> {
    const response = await Axios.get(
      `/planning-api/scenarios/${scenarioId}/zones`
    );
    return deserialize(response.data);
  }

  // TODO: can this be improved by using the `useEmissions` cache?
  return useQuery<LEZsResponse, APIError[], LEZ | undefined>(
    lezKeys.detail(scenarioId, lezId),
    () => queryFn(scenarioId),
    {
      select: ({ zones }) => {
        return zones.find((lez) => lez.id.toString() === lezId.toString());
      },
    }
  );
}

export function useLEZCreateMutation(scenarioId: number | string) {
  const queryClient = useQueryClient();

  async function mutationFn(
    scenarioId: number | string,
    data: LEZMutationData
  ) {
    const response = await Axios.post(
      `/planning-api/scenarios/${scenarioId}/zones`,
      data
    );
    return response.data;
  }

  return useMutation<LEZ, AxiosError<APIError[]>, LEZMutationData, unknown>(
    (values: LEZMutationData) => mutationFn(scenarioId, values),
    {
      onSettled: () => queryClient.invalidateQueries(lezKeys.all(scenarioId)),
    }
  );
}

export function useLEZUpdateMutation(
  scenarioId: number | string,
  lezId: number | string
) {
  const queryClient = useQueryClient();

  async function mutationFn(zoneId: number | string, data: LEZMutationData) {
    const response = await Axios.put(
      `/planning-api/zones/${zoneId}/generalSettings`,
      data
    );
    return response.data;
  }

  return useMutation<LEZ, AxiosError<APIError[]>, LEZMutationData, unknown>(
    (values: LEZMutationData) => mutationFn(lezId, values),
    {
      onSettled: async () => {
        await queryClient.invalidateQueries(lezKeys.all(scenarioId));
      },
    }
  );
}

export function useLEZDeleteMutation(
  scenarioId: number | string,
  lezId: number | string
) {
  const queryClient = useQueryClient();

  async function mutationFn(zoneId: number | string): Promise<void> {
    return Axios.delete(`/planning-api/zones/${zoneId}`);
  }

  return useMutation<void, AxiosError<APIError[]>, void, unknown>(
    () => mutationFn(lezId),
    {
      onSettled: () => {
        queryClient.invalidateQueries(lezKeys.all(scenarioId));
      },
    }
  );
}

export function useFleetConfig(
  lezId: number | string,
  restrictionFleetNameKey: string
) {
  async function queryFn(zoneId: number | string): Promise<FleetConfig> {
    const response = await Axios.get(
      `/planning-api/zones/${zoneId}/fleetrestriction`,
      {
        params: {
          restrictionFleetNameKey: restrictionFleetNameKey,
        },
      }
    );
    return response.data.map(deserialize);
  }
  return useQuery<FleetConfig, AxiosError<APIError[]>>(
    [...lezKeys.fleetConfig(lezId), restrictionFleetNameKey],
    () => queryFn(lezId),
    {
      suspense: false,
    }
  );
}

export function useFleetConfigUpdateMutation(
  lezId: number | string,
  scenarioId: number | string,
  restrictionFleetNameKey: string
) {
  const queryClient = useQueryClient();

  async function updateFleetConfig(
    zoneId: number | string,
    data: FleetConfig
  ): Promise<void> {
    const response = await Axios.put(
      `/planning-api/zones/${zoneId}/fleetrestriction`,
      { restrictionList: data, restrictionFleetNameKey }
    );
    return response.data;
  }

  return useMutation<void, AxiosError<APIError[]>, FleetConfig, unknown>(
    (values: FleetConfigMutationData) => updateFleetConfig(lezId, values),
    {
      onSuccess: async () => {
        queryClient.removeQueries({
          exact: false,
          queryKey: lezKeys.fleetConfig(lezId),
        });
        queryClient.removeQueries({
          exact: false,
          queryKey: lezKeys.fleetPercentageConfig(lezId),
        });
        await queryClient.invalidateQueries(lezKeys.detail(scenarioId, lezId));
      },
    }
  );
}

export function usePredefinedLEZOptions(caseId: number | string) {
  async function queryFn() {
    const response = await Axios.get<PredefinedLEZOption[]>(
      `/planning-api/cases/${caseId}/predefinedFleets`
    );
    return response.data;
  }
  return useQuery<PredefinedLEZOption[], AxiosError<APIError[]>>(
    lezKeys.predefinedLEZs(caseId),
    () => queryFn()
  );
}

export function usePercentageFleetsLEZOptions(caseId: number | string) {
  async function queryFn() {
    const response = await Axios.get<PredefinedLEZOption[]>(
      `/planning-api/cases/${caseId}/percentageFleets`
    );
    return response.data;
  }
  return useQuery<PredefinedLEZOption[], AxiosError<APIError[]>>(
    lezKeys.percentageFleets(caseId),
    () => queryFn()
  );
}

export function useRestrictionsFleetsLEZOptions(caseId: number | string) {
  async function queryFn() {
    const response = await Axios.get<PredefinedLEZOption[]>(
      `/planning-api/cases/${caseId}/restrictionFleets`
    );
    return response.data;
  }
  return useQuery<PredefinedLEZOption[], AxiosError<APIError[]>>(
    lezKeys.restrictionsFleets(caseId),
    () => queryFn()
  );
}

export function usePredefinedFleetConfigUpdateMutation(
  lezId: number | string,
  scenarioId: number | string
) {
  const queryClient = useQueryClient();

  async function mutationFn(
    zoneId: number | string,
    data: string
  ): Promise<void> {
    const response = await Axios.put(
      `/planning-api/zones/${zoneId}/predefinedFleet`,
      {
        predefinedFleetNameKey: data,
      }
    );
    return response.data;
  }

  return useMutation<void, AxiosError<APIError[]>, string, unknown>(
    (values: string) => mutationFn(lezId, values),
    {
      onSettled: async () => {
        await queryClient.invalidateQueries(lezKeys.detail(scenarioId, lezId));
        queryClient.removeQueries({
          exact: false,
          queryKey: lezKeys.fleetPercentageConfig(lezId),
        });
        queryClient.removeQueries({
          exact: false,
          queryKey: lezKeys.fleetConfig(lezId),
        });
      },
    }
  );
}

function groupFleetPercentage(
  data: FleetPercentageItem[]
): FleetPercentageCategory[] {
  const byCategory = new Map<
    string,
    {
      category: FleetPercentageCategory;
      fuels: Map<string, FleetPercentageItem[]>;
    }
  >();

  for (const item of data) {
    if (!byCategory.has(item.category)) {
      byCategory.set(item.category, {
        category: {
          category: item.category,
          categoryTranslation: item.categoryTranslation,
          categoryPercentage: item.categoryPercentage,
          fuels: [],
        },
        fuels: new Map(),
      });
    }
    const entry = byCategory.get(item.category);
    if (entry) {
      const fuelItems = entry.fuels.get(item.fuel) ?? [];
      fuelItems.push(item);
      entry.fuels.set(item.fuel, fuelItems);
    }
  }

  return Array.from(byCategory.values()).map(({ category, fuels }) => ({
    ...category,
    fuels: Array.from(fuels.entries()).map(([fuel, items]) => {
      const first = items[0];
      if (!first)
        return {
          fuel,
          fuelTranslation: "",
          fuelPercentage: 0,
          euros: [],
          categoryTranslation: category.categoryTranslation,
        };
      return {
        fuel,
        fuelTranslation: first.fuelTranslation,
        fuelPercentage: first.fuelPercentage,
        categoryTranslation: category.categoryTranslation,
        euros: items.map(({ id, euro, euroTranslation, euroPercentage }) => ({
          id,
          euro,
          euroTranslation,
          euroPercentage,
        })),
      };
    }),
  }));
}

function flattenFleetPercentageForApi(
  data: FleetPercentageCategory[]
): FleetPercentageApiItem[] {
  const result: FleetPercentageApiItem[] = [];
  for (const category of data) {
    for (const fuel of category.fuels) {
      for (const euro of fuel.euros) {
        result.push({
          id: euro.id,
          category: category.category,
          fuel: fuel.fuel,
          euro: euro.euro,
          fuelPercentage: fuel.fuelPercentage,
          euroPercentage: euro.euroPercentage,
        });
      }
    }
  }
  return result;
}

export function useFleetPercentageConfig(
  lezId: number | string,
  percentageFleetNameKey: string
) {
  async function queryFn(
    zoneId: number | string
  ): Promise<FleetPercentageCategory[]> {
    const response = await Axios.get<FleetPercentageItem[]>(
      `/planning-api/zones/${zoneId}/fleetpercentage`,
      {
        params: {
          percentageFleetNameKey: percentageFleetNameKey,
        },
      }
    );
    return groupFleetPercentage(response.data);
  }
  return useQuery<FleetPercentageCategory[], AxiosError<APIError[]>>(
    [...lezKeys.fleetPercentageConfig(lezId), percentageFleetNameKey],
    () => queryFn(lezId),
    { suspense: false }
  );
}

export function useFleetPercentageConfigUpdateMutation(
  lezId: number | string,
  scenarioId: number | string,
  percentageFleetNameKey: string
) {
  const queryClient = useQueryClient();

  async function mutationFn(
    zoneId: number | string,
    data: FleetPercentageCategory[]
  ): Promise<void> {
    const payload = flattenFleetPercentageForApi(data);

    const response = await Axios.put(
      `/planning-api/zones/${zoneId}/fleetpercentage`,
      { percentageList: payload, percentageFleetNameKey }
    );
    return response.data;
  }

  return useMutation<
    void,
    AxiosError<APIError[]>,
    FleetPercentageCategory[],
    unknown
  >((values: FleetPercentageCategory[]) => mutationFn(lezId, values), {
    onSuccess: async () => {
      queryClient.removeQueries({
        exact: false,
        queryKey: lezKeys.fleetPercentageConfig(lezId),
      });
      queryClient.removeQueries({
        exact: false,
        queryKey: lezKeys.fleetConfig(lezId),
      });

      await queryClient.invalidateQueries(lezKeys.detail(scenarioId, lezId));
    },
  });
}
